import { msg } from '@lingui/core/macro';

import { defineFilters } from '@/shared/filters/define-filters';

import { FUND_LIST_SENDS_FINAL_DATE, maskDocumentLikeLegacy } from './fund.constants';

// Filtros da lista (discovery/01 §2.3, `FundFilters/*` do legado), declarados uma vez. Entra no
// arquivo da rota (JS inicial): só importa o que é leve.

/** Filtros cuja ordem vai ao fio (F4); o período conta pela chave do início. */
export const FUND_FILTER_ORDER_KEYS = ['searchString', 'name', 'registrationNumber', 'email', 'initialDate'] as const;
export type FundFilterOrderKey = (typeof FUND_FILTER_ORDER_KEYS)[number];

const orderKeyOf = (key: string): FundFilterOrderKey | undefined => {
  const base = key === 'finalDate' ? 'initialDate' : key;
  return (FUND_FILTER_ORDER_KEYS as readonly string[]).includes(base) ? (base as FundFilterOrderKey) : undefined;
};

/** A ordem dos filtros numa busca da URL (a ordem das chaves), sem repetir o período. */
export function fundFilterOrder(search: Readonly<Record<string, unknown>>): FundFilterOrderKey[] {
  const order: FundFilterOrderKey[] = [];
  for (const [key, value] of Object.entries(search)) {
    const base = orderKeyOf(key);
    if (base && value !== undefined && value !== '' && !order.includes(base)) order.push(base);
  }
  return order;
}

/** Reescreve os filtros na ordem pedida (o resto fica depois, na ordem em que veio). */
export function orderFundFilters<T extends Record<string, unknown>>(filters: T, order: readonly FundFilterOrderKey[] | undefined): T {
  if (!order?.length) return filters;
  const out: Record<string, unknown> = {};
  for (const key of order) {
    for (const own of key === 'initialDate' ? ['initialDate', 'finalDate'] : [key]) if (own in filters) out[own] = filters[own];
  }
  for (const [key, value] of Object.entries(filters)) if (!(key in out)) out[key] = value;
  return out as T;
}

/**
 * Aplica um patch dos filtros na busca como o `addFilter` do legado (`filterContext.tsx:236-268`):
 * filtro removido sai, filtro novo ou com valor novo vai para o fim, o que não mudou fica no lugar.
 */
export function applyFundFilterPatch(previous: Readonly<Record<string, unknown>>, patch: Readonly<Record<string, unknown>>): Record<string, unknown> {
  const next = new Map(Object.entries(previous));
  for (const [key, value] of Object.entries(patch)) {
    if (value !== undefined && next.has(key) && JSON.stringify(next.get(key)) === JSON.stringify(value)) continue;
    // `Map` reinserido vai para o fim, como o `push` do legado.
    next.delete(key);
    if (value !== undefined) next.set(key, value);
  }
  return Object.fromEntries(next);
}

export const fundFilters = defineFilters([
  { key: 'searchString', type: 'search', label: msg`Todos`, placeholder: msg`Buscar por nome, CNPJ ou e-mail` },
  { key: 'name', type: 'text', label: msg`Nome` },
  // U21: o filtro vai ao fio com a máscara progressiva do legado.
  { key: 'registrationNumber', type: 'document', label: msg`CPF/CNPJ`, toParam: maskDocumentLikeLegacy },
  { key: 'email', type: 'text', label: msg`Email` },
  {
    key: 'initialDate',
    to: 'finalDate',
    type: 'dateRange',
    label: msg`Criado em`,
    fromLabel: msg`Criado de`,
    toLabel: msg`até`,
    pinned: true,
    sendTo: FUND_LIST_SENDS_FINAL_DATE, // U20-1
  },
]);
